<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import V2WizardSteps from '@/components/modals/printing/wizard/V2WizardSteps.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  /**
   * The flow's top bar (plan §4.0): a brand-neutral store tile, the title,
   * the four steps on md+ (the words «Paso 2 de 4: Negocio» and a thin track
   * below md), an `actions` slot and «Salir».
   */
  const STEP_KEYS = ['location', 'business', 'menu', 'orders'] as const;

  const props = withDefaults(
    defineProps<{
      title: string;
      /** 0..3; null hides the steps (Live screen, `/menu/import`). */
      step?: number | null;
      exitDisabled?: boolean;
    }>(),
    { step: null, exitDisabled: false }
  );
  const emit = defineEmits<{ (e: 'exit'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const labels = computed(() =>
    STEP_KEYS.map((key) => tr(`storeSetup.steps.${key}`))
  );
  const stepIndex = computed(() =>
    props.step !== null && props.step >= 0 && props.step < STEP_KEYS.length
      ? props.step
      : null
  );
  const stepLine = computed(() => {
    const index = stepIndex.value;
    if (index === null) return '';
    return index === STEP_KEYS.length - 1
      ? tr('storeSetup.lastStep')
      : tr('storeSetup.stepLine', {
          step: index + 1,
          total: STEP_KEYS.length,
          name: labels.value[index],
        });
  });
  const progress = computed(() =>
    stepIndex.value === null
      ? 0
      : ((stepIndex.value + 1) / STEP_KEYS.length) * 100
  );
</script>

<template>
  <header class="v2-setup-top-bar">
    <div class="bar-main" :class="{ 'has-actions': !!$slots.actions }">
      <span class="brand-tile" aria-hidden="true">
        <PosUiIcon name="storefront-outline" :size="18" />
      </span>
      <p class="bar-title">{{ title }}</p>
      <div v-if="stepIndex !== null" class="bar-steps">
        <V2WizardSteps :steps="labels" :current="stepIndex" />
      </div>
      <div v-if="$slots.actions" class="bar-actions">
        <slot name="actions" />
      </div>
      <PosUiButton
        class="exit-button"
        color="neutral"
        variant="ghost"
        icon="close"
        :disabled="exitDisabled"
        @click="emit('exit')"
        >{{ $t('storeSetup.exit') }}</PosUiButton
      >
    </div>
    <div v-if="stepIndex !== null" class="bar-step-line">
      <!-- In the accessibility tree on every width: the md+ indicator has no text alternative. -->
      <p class="step-line-text">{{ stepLine }}</p>
      <span class="step-track" aria-hidden="true">
        <span class="step-fill" :style="{ width: `${progress}%` }" />
      </span>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .v2-setup-top-bar {
    @apply tw-shrink-0 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 md:tw-px-6;
    .bar-main {
      @apply tw-flex tw-min-h-14 tw-items-center tw-gap-3;
      // Only the current step keeps its label (the words stay in the sr-only
      // step line), so the title keeps its room: on tablets, and below xl
      // when the row has actions (the menu's save state, «Deshacer», ⋯).
      @media (max-width: 1023px) {
        .bar-steps {
          flex-basis: 16rem;
          :deep(.wizard-step:not(.active) .wizard-step-label) {
            @apply tw-hidden;
          }
        }
      }
      &.has-actions .bar-steps {
        @media (max-width: 1279px) {
          flex-basis: 16rem;
          :deep(.wizard-step:not(.active) .wizard-step-label) {
            @apply tw-hidden;
          }
        }
      }
    }
    .brand-tile {
      @apply tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-900 tw-text-white;
    }
    // Its own width first: the steps give way before the title is cut, so
    // the ellipsis only shows for a truly long name.
    .bar-title {
      @apply tw-m-0 tw-min-w-0 tw-truncate tw-text-base tw-font-semibold tw-text-neutral-900;
      flex: 1 1 auto;
    }
    // The indicator's own labels show from md; the whole row only exists there.
    .bar-steps {
      @apply tw-hidden md:tw-flex;
      // Shrinks well before the title (12 to 1), never below its own content
      // (the automatic minimum width): the dots, the labels shown, 12 px lines.
      flex: 0 12 30rem;
      :deep(.v2-wizard-steps) {
        @apply tw-w-full;
      }
    }
    .bar-actions {
      @apply tw-flex tw-shrink-0 tw-items-center tw-gap-2;
    }
    .exit-button {
      @apply tw-shrink-0;
      :deep(.pos-ui-button-label) {
        @apply tw-sr-only md:tw-not-sr-only md:tw-inline-flex;
      }
    }
    .bar-step-line {
      @apply tw-flex tw-items-center tw-gap-3 tw-pb-2.5 md:tw-sr-only;
    }
    .step-line-text {
      @apply tw-m-0 tw-shrink-0 tw-text-xs tw-font-medium tw-text-neutral-600;
    }
    .step-track {
      @apply tw-h-1 tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-neutral-200;
    }
    // Active-state indicator: the one other place orange is allowed.
    .step-fill {
      @apply tw-block tw-h-full tw-rounded-full tw-bg-orange-600;
    }
  }
</style>
